import React, { useEffect, useState, useRef } from 'react';
import { Table, Tag, Typography, Space } from 'antd';
import axios from 'axios';
import { DollarCircleOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL } from '../api';
import OrdersTable from './OrdersTable';
import RiskAnalysisBar from './RiskAnalysisBar';
import App from '../App.css';
import { Spin } from "antd"; // Import Spin for loading state

const CurrentPositions = () => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef();

  // Fetch positions: show spinner only on first load
  const fetchPositions = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/current-positions`, { withCredentials: true });
      setPositions(res.data);
    } catch (err) {
      console.error('Error fetching positions:', err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    // Initial load: show spinner
    fetchPositions(true);
    // Subsequent refreshes: no spinner
    intervalRef.current = setInterval(() => fetchPositions(false), 5000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const columns = [
    {
      title: <span style={{ color: 'black', fontWeight: 600 }}>Symbol</span>,
      dataIndex: 'symbol',
      key: 'symbol',
      render: symbol => (
        <Space>
          <DollarCircleOutlined style={{ color: 'black' }} />
          <span style={{ fontWeight: 500, color: 'black' }}>{symbol}</span>
        </Space>
      ),
    },
    {
      title: <span style={{ color: 'black', fontWeight: 600 }}>Side</span>,
      dataIndex: 'side',
      key: 'side',
      render: side => (
        <Tag color={side === 'Buy' ? 'green' : 'red'} style={{ fontWeight: 500, color: 'white', background: side === 'Buy' ? 'green' : 'red', border: 0 }}>
          {side === 'Buy' ? 'Long' : 'Short'}
        </Tag>
      ),
    },
    {
      title: <span style={{ color: 'black', fontWeight: 600 }}>Avg Price</span>,
      dataIndex: 'avgPrice',
      key: 'avgPrice',
      render: price => <span style={{ color: 'black', fontWeight: 500 }}>{price}</span>,
    },
    {
      title: <span style={{ color: 'black', fontWeight: 600 }}>Mark Price</span>,
      dataIndex: 'markPrice',
      key: 'markPrice',
      render: price => <span style={{ color: 'black', fontWeight: 500 }}>{price}</span>,
    },
    {
      title: <span style={{ color: 'black', fontWeight: 600 }}>Unrealised PnL</span>,
      dataIndex: 'unrealisedPnl',
      key: 'unrealisedPnl',
      render: pnl => {
        const isProfit = parseFloat(pnl) >= 0;
        return (
          <Tag
            color={isProfit ? 'green' : 'red'}
            style={{ fontWeight: 600, fontSize: 16, color: 'white', background: isProfit ? 'green' : 'red', border: 0 }}
            icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          >
            <div className='pnl-numbers'>{pnl}</div>
          </Tag>
        );
      },
    },
    {
      title: <span style={{ color: 'black', fontWeight: 600 }}>Cur Realised PnL</span>,
      dataIndex: 'curRealisedPnl',
      key: 'curRealisedPnl',
      render: pnl => {
        const isProfit = parseFloat(pnl) >= 0;
        return (
          <Tag
            color={isProfit ? 'green' : 'red'}
            style={{
              fontWeight: 600,
              fontSize: 16,
              color: 'white',
              background: isProfit ? 'green' : 'red',
              border: 0
            }}
            icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          >
           <div className='pnl-numbers'>{pnl}</div>
          </Tag>
        );
      },
    },
    // {
    //   title: <span style={{ color: 'black', fontWeight: 600 }}>Position Value</span>,
    //   dataIndex: 'positionValue',
    //   key: 'positionValue',
    //   render: value => (
    //     <span style={{ color: 'black', fontWeight: 600 }}>${parseFloat(value).toFixed(4)}</span>
    //   ),
    // },
    {
      title: <span style={{ color: 'black', fontWeight: 600 }}>Size</span>,
      dataIndex: 'size',
      key: 'size',
      render: size => (
        <span style={{ fontWeight: 600, color: 'black', fontSize: 15 }}>{size}</span>
      ),
    },
    {
      title: <span style={{ color: 'black', fontWeight: 600 }}>Leverage</span>,
      dataIndex: 'leverage',
      key: 'leverage',
      render: lev => <span style={{ color: 'black', fontWeight: 500 }}>{lev}x</span>,
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Sort positions alphabetically by symbol (A-Z)
  const sortedPositions = [...positions].sort((a, b) => {
    if (!a.symbol || !b.symbol) return 0;
    return a.symbol.localeCompare(b.symbol);
  });

  return (
    <div>
      <div style={{ margin: '0 20px', width: 'calc(100% - 40px)' }}>
        {/* Risk Analysis Bar */}
        <RiskAnalysisBar />
        
        <Typography.Title level={2} style={{ textAlign: 'center', color: 'black', marginBottom: 32, fontWeight: 700, letterSpacing: 1, marginTop: 10 }}>
          {/* 🚀 Live Crypto Positions */}
        </Typography.Title>
        <Table
          dataSource={sortedPositions}
          columns={columns}
          rowKey={(record) => `${record.symbol}-${record.side}`}
          pagination={false}
          bordered
          size="middle"
          style={{ background: 'white', width: '100%' }}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{
                margin: 0,
                padding: '0 0 16px 0',
                width: '100%',
                minHeight: 'auto',
                overflow: 'visible'
              }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: 16,
                  color: '#222',
                  marginBottom: 12,
                  paddingLeft: 16
                }}>
                  Related Orders - {record.side === 'Buy' ? 'Long' : 'Short'} Position:
                </div>

                <div style={{
                  width: '100%',
                  overflow: 'visible',
                  minHeight: 'fit-content'
                }}>
                  <OrdersTable
                    active={true}
                    orderType="normal"
                    token={record.symbol}
                    side={record.side}
                    compact={true}
                    showPagination={false}
                    expandedView={true}
                    filterStatus="Filled"
                  />
                </div>
              </div>
            ),
            rowExpandable: () => true,
            expandRowByClick: true,
          }}
        />
      </div>

      <ToastContainer 
        position="top-right" 
        autoClose={5000} 
        hideProgressBar={false} 
        newestOnTop 
        closeOnClick 
        rtl={false} 
        pauseOnFocusLoss 
        draggable 
        pauseOnHover 
      />
    </div>
  );
};

export default CurrentPositions;





// import React, { useEffect, useState, useRef } from 'react';
// import { Table, Tag, Typography, Space, Badge, Modal, Input, Button } from 'antd';
// import axios from 'axios';
// import { DollarCircleOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { BASE_URL } from '../api';
// import StopLossPopup from './StopLossPopup';
// import TakeProfitPopup from './TakeProfitPopup';
// import App from '../App.css'; // Assuming you have a CSS file for styles



// const CurrentPositions = () => {
//   const [positions, setPositions] = useState([]);
//   const [orders, setOrders] = useState([]);
//   const [slPopup, setSlPopup] = useState({ visible: false, order: null, currentPrice: '' });
//   const [tpPopup, setTpPopup] = useState({ visible: false, order: null, currentPrice: '' });
//   const [closeModal, setCloseModal] = useState({ visible: false, position: null });
//   const [closeQty, setCloseQty] = useState('');
//   const [closeLoading, setCloseLoading] = useState(false);

//   // Fetch orders for StopLossPopup
//   const fetchOrders = async () => {
//     try {
//       const res = await axios.get(`${BASE_URL}/api/orders`, { withCredentials: true });
//       setOrders(res.data);
//     } catch (err) {
//       console.error('Error fetching orders:', err);
//     }
//   };

//   // Auto-refresh every second
//   const intervalRef = useRef();
//   useEffect(() => {
//     const fetchPositionsAndOrders = () => {
//       axios.get(`${BASE_URL}/api/current-positions`, { withCredentials: true })
//         .then(res => setPositions(res.data))
//         .catch(err => console.error('Error fetching positions:', err));
//       axios.get(`${BASE_URL}/api/orders`, { withCredentials: true })
//         .then(res => setOrders(res.data))
//         .catch(err => console.error('Error fetching orders:', err));
//     };
//     fetchPositionsAndOrders();
//     intervalRef.current = setInterval(fetchPositionsAndOrders, 1000);
//     return () => clearInterval(intervalRef.current);
//   }, []);

//   const columns = [
  
//     {
//       title: <span style={{ color: 'black', fontWeight: 600 }}>Symbol</span>,
//       dataIndex: 'symbol',
//       key: 'symbol',
//       render: symbol => (
//         <Space>
//           <DollarCircleOutlined style={{ color: 'black' }} />
//           <span style={{ fontWeight: 500, color: 'black' }}>{symbol}</span>
//         </Space>
//       ),
//     },
//     {
//       title: <span style={{ color: 'black', fontWeight: 600 }}>Side</span>,
//       dataIndex: 'side',
//       key: 'side',
//       render: side => (
//         <Tag color={side === 'Buy' ? 'green' : 'red'} style={{ fontWeight: 500, color: 'white', background: side === 'Buy' ? 'green' : 'red', border: 0 }}>
//           {/* {side} */}
//            {side === 'Buy' ? 'Long' : 'Short'}
//         </Tag>
//       ),
//     },
//     {
//       title: <span style={{ color: 'black', fontWeight: 600 }}>Avg Price</span>,
//       dataIndex: 'avgPrice',
//       key: 'avgPrice',
//       render: price => <span style={{ color: 'black', fontWeight: 500 }}>{price}</span>,
//     },
//     {
//       title: <span style={{ color: 'black', fontWeight: 600 }}>Mark Price</span>,
//       dataIndex: 'markPrice',
//       key: 'markPrice',
//       render: price => <span style={{ color: 'black', fontWeight: 500 }}>{price}</span>,
//     },
//     {
//       title: <span style={{ color: 'black', fontWeight: 600 }}>Unrealised PnL</span>,
//       dataIndex: 'unrealisedPnl',
//       key: 'unrealisedPnl',
//       render: pnl => {
//         const isProfit = parseFloat(pnl) >= 0;
//         return (
//           <Tag
//             color={isProfit ? 'green' : 'red'}
//             style={{ fontWeight: 600, fontSize: 16, color: 'white', background: isProfit ? 'green' : 'red', border: 0 }}
//             icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
//           >
//             {pnl}
//              {/* <div className="pnl-numbers">{pnl}</div> */}
//           </Tag>
//         );
//       },
//     },
//     // {
//     //   title: <span style={{ color: 'black', fontWeight: 600 }}>Cum Realised PnL</span>,
//     //   dataIndex: 'cumRealisedPnl',
//     //   key: 'cumRealisedPnl',
//     //   render: pnl => {
//     //     const isProfit = parseFloat(pnl) >= 0;
//     //     return (
//     //       <Tag
//     //         color={isProfit ? 'green' : 'red'}
//     //         style={{ fontWeight: 600, fontSize: 16, color: 'white', background: isProfit ? 'green' : 'red', border: 0 }}
//     //       >
//     //         <div className="pnl-numbers">{pnl}</div>
//     //       </Tag>
//     //     );
//     //   },
//     // },
//     // {
//     //   title: <span style={{ color: 'black', fontWeight: 600 }}>Cur Realised PnL</span>,
//     //   dataIndex: 'curRealisedPnl',
//     //   key: 'curRealisedPnl',
//     //   render: pnl => {
//     //     const isProfit = parseFloat(pnl) >= 0;
//     //     return (
//     //       <Tag
//     //         color={isProfit ? 'green' : 'red'}
//     //         style={{ fontWeight: 600, fontSize: 16, color: 'white', background: isProfit ? 'green' : 'red', border: 0 }}
//     //       >
//     //         <div className="pnl-numbers">{pnl}</div>
//     //       </Tag>
//     //     );
//     //   },
//     // },
// //     {
// //   title: <span style={{ color: 'black', fontWeight: 600 }}>Cum Realised PnL</span>,
// //   dataIndex: 'cumRealisedPnl',
// //   key: 'cumRealisedPnl',
// //   render: pnl => {
// //     const isProfit = parseFloat(pnl) >= 0;
// //     return (
// //       <Tag
// //         color={isProfit ? 'green' : 'red'}
// //         style={{
// //           fontWeight: 600,
// //           fontSize: 16,
// //           color: 'white',
// //           background: isProfit ? 'green' : 'red',
// //           border: 0
// //         }}
// //         icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
// //       >
// //         {/* <div className="pnl-numbers">{pnl}</div> */}
// //         {pnl}
// //       </Tag>
// //     );
// //   },
// // },
// {
//   title: <span style={{ color: 'black', fontWeight: 600 }}>Cur Realised PnL</span>,
//   dataIndex: 'curRealisedPnl',
//   key: 'curRealisedPnl',
//   render: pnl => {
//     const isProfit = parseFloat(pnl) >= 0;
//     return (
//       <Tag
//         color={isProfit ? 'green' : 'red'}
//         style={{
//           fontWeight: 600,
//           fontSize: 16,
//           color: 'white',
//           background: isProfit ? 'green' : 'red',
//           border: 0
//         }}
//         icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
//       >
//         {/* <div className="pnl-numbers"></div> */}
//         {pnl}
//       </Tag>
//     );
//   },
// },
//     {
//       title: <span style={{ color: 'black', fontWeight: 600 }}>Position Value</span>,
//       dataIndex: 'positionValue',
//       key: 'positionValue',
//       render: value => (
//         <span style={{ color: 'black', fontWeight: 600 }}>${parseFloat(value).toFixed(4)}</span>
//       ),
//     },
//     // {
//     //   title: '',
//     //   key: 'close',
//     //   render: (text, record) => (
//     //     <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
//     //       <Button
//     //         style={{ background: 'red', color: 'white', fontWeight: 600, border: 0 }}
//     //         onClick={() => {
//     //           setCloseModal({ visible: true, position: record });
//     //           setCloseQty('');
//     //         }}
//     //         size="small"
//     //       >
//     //         Close
//     //       </Button>
//     //     </span>
//     //   ),
//     // },
//     {
//       title: <span style={{ color: 'black', fontWeight: 600 }}>Size</span>,
//       dataIndex: 'size',
//       key: 'size',
//       render: size => (
//         <span style={{ fontWeight: 600, color: 'black', fontSize: 15 }}>{size}</span>
//       ),
//     },
//     {
//       title: <span style={{ color: 'black', fontWeight: 600 }}>Leverage</span>,
//       dataIndex: 'leverage',
//       key: 'leverage',
//       render: lev => <span style={{ color: 'black', fontWeight: 500 }}>{lev}x</span>,
//     },
//   ];

//   return (
//     <div>
//       <div style={{ maxWidth: 1100, margin: '0 auto' }}>
//         <Typography.Title level={2} style={{ textAlign: 'center', color: 'black', marginBottom: 32, fontWeight: 700, letterSpacing: 1, marginTop:10 }}>
//           🚀 Live Crypto Positions
//         </Typography.Title>
//         <Table
//           dataSource={positions}
//           columns={columns}
//           rowKey="symbol"
//           pagination={false}
//           bordered
//           size="middle"
//           style={{ background: 'white' }}
//           expandable={{
//             // expandedRowRender: (record) => {
//             //   // Main orders for this position
//             //   const relatedOrders = orders
//             //     .filter(o => o.token === record.symbol && o.orderStatus && o.orderStatus.toLowerCase() === 'filled' && o.orderNature === 'normal' && o.LinkedOrderId == null)
//             //     .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
//             //   // Sub-orders (TP/SL) for this position
//             //   // Attach markPrice from main position to each subOrder
//             //   const subOrders = orders
//             //     .filter(o => o.token === record.symbol && o.LinkedOrderId && (o.orderNature === 'tp' || o.orderNature === 'sl'))
//             //     .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
//             //     .map(subOrder => ({ ...subOrder, markPrice: record.markPrice }));
//             //   if (!relatedOrders.length && !subOrders.length) return <span style={{ color: '#888' }}>No filled orders for this position.</span>;
//             //   return (
//             //     <>
//             //       {relatedOrders.length > 0 && (
//             //         <Table
//             //           dataSource={relatedOrders}
//             //           pagination={false}
//             //           size="small"
//             //           rowKey="_id"
//             //           columns={[
//             //             { title: <span style={{ color: 'black', fontWeight: 600 }}>Signal By</span>, dataIndex: 'signalBy', key: 'signalBy' },
//             //             { title: <span style={{ color: 'black', fontWeight: 600 }}>Side</span>, dataIndex: 'side', key: 'side', render: side => (
//             //               <Tag color={side === 'Buy' ? 'green' : 'red'} style={{ fontWeight: 500, color: 'white', background: side === 'Buy' ? 'green' : 'red', border: 0 }}>{side === 'Buy' ? 'Long' : 'Short'}</Tag>
//             //             ) },
//             //             { title: <span style={{ color: 'black', fontWeight: 600 }}>Avg Price</span>, dataIndex: 'avgPrice', key: 'avgPrice', render: price => <span style={{ color: 'black', fontWeight: 500 }}>{price}</span> },
//             //             { title: <span style={{ color: 'black', fontWeight: 600 }}>Entry Price</span>, dataIndex: 'entryPrice', key: 'entryPrice', render: price => <span style={{ color: 'black', fontWeight: 500 }}>{price}</span> },
//             //             { title: <span style={{ color: 'black', fontWeight: 600 }}>Mark Price</span>, dataIndex: 'markPrice', key: 'markPrice', render: (price, subOrder) => <span style={{ color: 'black', fontWeight: 500 }}>{subOrder.markPrice}</span> },
//             //             { title: <span style={{ color: 'black', fontWeight: 600 }}>Unrealised PnL</span>, key: 'sub_unrealisedPnl', render: () => <span style={{ color: '#888' }}></span> },
//             //             { title: <span style={{ color: 'black', fontWeight: 600 }}>Cum Realised PnL</span>, key: 'sub_cumRealisedPnl', render: () => <span style={{ color: '#888' }}></span> },
//             //             { title: <span style={{ color: 'black', fontWeight: 600 }}>Cur Realised PnL</span>, key: 'sub_curRealisedPnl', render: () => <span style={{ color: '#888' }}></span> },
//             //             { title: <span style={{ color: 'black', fontWeight: 600 }}>Position Value</span>, key: 'sub_positionValue', render: () => <span style={{ color: '#888' }}></span> },
//             //             // Insert Stop Loss button before Size column
//             //             {
//             //                 title: <span style={{ color: 'black', fontWeight: 600 }}></span>,
//             //                 key: 'takeProfitBtn',
//             //                 render: (text, subOrder) => (
//             //                     <Button
//             //                       size="small"
//             //                       style={{ marginRight: 8, background: '#5ba66f', color: 'white' }}
//             //                       onClick={async () => {
//             //                         let currentPrice = '';
//             //                         try {
//             //                           const res = await fetch(`https://api.bybit.com/v5/market/tickers?category=linear&symbol=${subOrder.token}`);
//             //                           const data = await res.json();
//             //                           currentPrice = data?.result?.list?.[0]?.lastPrice || '';
//             //                         } catch {}
//             //                         setTpPopup({ visible: true, order: subOrder, currentPrice });
//             //                       }}
//             //                     >Take Profit</Button>
                              
//             //                 )
//             //               },
//             //             {
//             //               title: <span style={{ color: 'black', fontWeight: 600 }}></span>,
//             //               key: 'stopLoss',
//             //               render: (text, orderRecord) => (
//             //                 <Button
//             //                   size="small"
//             //                   style={{ marginLeft: 8, background: '#ef4444', color: 'white' }}
//             //                   onClick={() => setSlPopup({ visible: true, order: orderRecord, currentPrice: orderRecord.markPrice || orderRecord.entryPrice })}
//             //                 >Stop Loss</Button>
//             //               )
//             //             },
//             //             { title: <span style={{ color: 'black', fontWeight: 600 }}>Size</span>, dataIndex: 'count', key: 'count', render: size => <span style={{ fontWeight: 600, color: 'black', fontSize: 15 }}>{size}</span> },
//             //             { title: <span style={{ color: 'black', fontWeight: 600 }}>Leverage</span>, dataIndex: 'leverage', key: 'leverage', render: lev => <span style={{ color: 'black', fontWeight: 500 }}>{lev ? lev + 'x' : ''}</span> },
//             //           ]}
//             //           style={{ margin: '12px 0', background: '#fafafa', borderRadius: 6 }}
//             //         />
//             //       )}
//             //       {subOrders.length > 0 && (
//             //         <div style={{ marginTop: 12 }}>
//             //           <div style={{ fontWeight: 600, fontSize: 15, color: '#222', marginBottom: 6 }}>TP/SL Orders:</div>
//             //           {/* <Table
//             //             dataSource={subOrders}
//             //             pagination={false}
//             //             size="small"
//             //             rowKey="_id"
//             //             columns={[ 
//             //               { title: <span style={{ color: 'black', fontWeight: 600 }}>Type</span>, dataIndex: 'orderNature', key: 'orderNature', render: val => val === 'tp' ? 'Take Profit' : 'Stop Loss' },
//             //               { title: <span style={{ color: 'black', fontWeight: 600 }}>Qty</span>, dataIndex: 'count', key: 'count' },
//             //               { title: <span style={{ color: 'black', fontWeight: 600 }}>Price</span>, dataIndex: 'stopLoss', key: 'stopLoss' },
//             //               { title: <span style={{ color: 'black', fontWeight: 600 }}>Status</span>, dataIndex: 'orderStatus', key: 'orderStatus' },
  
//             //               { title: <span style={{ color: 'black', fontWeight: 600 }}>Size</span>, key: 'size', render: subOrder => <span style={{ fontWeight: 600, color: 'black', fontSize: 15 }}>{subOrder.count}</span> },
//             //               {
//             //                 title: <span style={{ color: 'black', fontWeight: 600 }}>Take Profit</span>,
//             //                 key: 'takeProfitBtn',
//             //                 render: (text, subOrder) => (
//             //                   subOrder.orderNature === 'tp' ? (
//             //                     <Button
//             //                       size="small"
//             //                       style={{ marginRight: 8, background: '#5ba66f', color: 'white' }}
//             //                       onClick={async () => {
//             //                         let currentPrice = '';
//             //                         try {
//             //                           const res = await fetch(`https://api.bybit.com/v5/market/tickers?category=linear&symbol=${subOrder.token}`);
//             //                           const data = await res.json();
//             //                           currentPrice = data?.result?.list?.[0]?.lastPrice || '';
//             //                         } catch {}
//             //                         setTpPopup({ visible: true, order: subOrder, currentPrice });
//             //                       }}
//             //                     >Take Profit</Button>
//             //                   ) : null
//             //                 )
//             //               },
//             //               {
//             //                 title: <span style={{ color: 'black', fontWeight: 600 }}>Stop Loss</span>,
//             //                 key: 'stopLossBtn',
//             //                 render: (text, subOrder) => (
//             //                   subOrder.orderNature === 'sl' ? (
//             //                     <Button
//             //                       size="small"
//             //                       style={{ marginLeft: 8, background: '#ef4444', color: 'white' }}
//             //                       onClick={() => setSlPopup({ visible: true, order: subOrder, currentPrice: subOrder.stopLoss || subOrder.entryPrice })}
//             //                     >Stop Loss</Button>
//             //                   ) : null
//             //                 )
//             //               },
//             //             ]}
//             //             style={{ background: '#f9f9f9', borderRadius: 6 }}
//             //           /> */}
//             //         </div>
//             //       )}
//             //     </>
//             //   );
//             // },
//             expandedRowRender: (record) => {
//   // Main orders for this position
//   // Helper to get sold quantity for a main order
//   const getSoldQty = (mainOrder) => {
//     const filtered = orders.filter(o =>
//       o.LinkedOrderId === mainOrder._id &&
//       (o.orderNature === 'tp' || o.orderNature === 'sl') &&
//       o.orderStatus && ['filled', 'triggered'].includes(o.orderStatus.toLowerCase())
//     );
//     return filtered.reduce((sum, o) => {
//       let qty = o.filledQuantity;
//       if (typeof qty === 'string') qty = qty.trim();
//       const num = qty === '' || qty == null ? 0 : parseFloat(qty);
//       return sum + (isNaN(num) ? 0 : num);
//     }, 0);
//   };

//   const relatedOrders = orders
//     .filter(o => {
//       if (!(o.token === record.symbol && o.orderStatus && o.orderStatus.toLowerCase() === 'filled' && o.orderNature === 'normal' && o.LinkedOrderId == null)) return false;
//       const filledQty = parseFloat(o.filledQuantity) || parseFloat(o.count) || 0;
//       // Use soldQuantity from the order itself if present, else fallback to getSoldQty
//       const soldQty = o.soldQuantity !== undefined && o.soldQuantity !== null && o.soldQuantity !== '' ? parseFloat(o.soldQuantity) : getSoldQty(o);
//       return (filledQty - soldQty) !== 0;
//     })
//     .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  
//   // Sub-orders (TP/SL) for this position
//   // Attach markPrice from main position to each subOrder
//   const subOrders = orders
//     .filter(o => o.token === record.symbol && o.LinkedOrderId && (o.orderNature === 'tp' || o.orderNature === 'sl'))
//     .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
//     .map(subOrder => ({ 
//       ...subOrder, 
//       markPrice: record.markPrice  // This adds the markPrice from the main position
//     }));
//     // console.log('subOrders', subOrders);
//   if (!relatedOrders.length && !subOrders.length) return <span style={{ color: '#888' }}>No filled orders for this position.</span>;
  
//   return (
//     <>
//       {relatedOrders.length > 0 && (
//         <Table
//           dataSource={relatedOrders}
//           pagination={false}
//           size="small"
//           rowKey="_id"
//           columns={[
//             { title: <span style={{ color: 'black', fontWeight: 600 }}>Signal By</span>, dataIndex: 'Algorithm', key: 'Algorithm' },
//             { title: <span style={{ color: 'black', fontWeight: 600 }}>Side</span>, dataIndex: 'side', key: 'side', render: side => (
//               <Tag color={side === 'Buy' ? 'green' : 'red'} style={{ fontWeight: 500, color: 'white', background: side === 'Buy' ? 'green' : 'red', border: 0 }}>{side === 'Buy' ? 'Long' : 'Short'}</Tag>
//             ) },
//             { title: <span style={{ color: 'black', fontWeight: 600 }}>Avg Price</span>, dataIndex: 'avgPrice', key: 'avgPrice', render: price => <span style={{ color: 'black', fontWeight: 500 }}>{price}</span> },
//             // { title: <span style={{ color: 'black', fontWeight: 600 }}>Entry Price</span>, dataIndex: 'entryPrice', key: 'entryPrice', render: price => <span style={{ color: 'black', fontWeight: 500 }}>{price}</span> },
//             { 
//               title: <span style={{ color: 'black', fontWeight: 600 }}>Mark Price</span>, 
//               dataIndex: 'markPrice', 
//               key: 'markPrice', 
//               render: (price, order) => <span style={{ color: 'black', fontWeight: 500 }}>{order.markPrice || record.markPrice}</span> 
//             },
//             { title: <span style={{ color: 'black', fontWeight: 600 }}>Unrealised PnL</span>, key: 'sub_unrealisedPnl', render: () => <span style={{ color: '#888' }}></span> },
//             { title: <span style={{ color: 'black', fontWeight: 600 }}>Cum Realised PnL</span>, key: 'sub_cumRealisedPnl', render: () => <span style={{ color: '#888' }}></span> },
//             { title: <span style={{ color: 'black', fontWeight: 600 }}>Cur Realised PnL</span>, key: 'sub_curRealisedPnl', render: () => <span style={{ color: '#888' }}></span> },
//             { title: <span style={{ color: 'black', fontWeight: 600 }}>Position Value</span>, key: 'sub_positionValue', render: (text, subOrder) => {
//                 const value = parseFloat(subOrder.avgPrice) * parseFloat(subOrder.count);
//                 return <span style={{ color: 'black', fontWeight: 600 }}>${isNaN(value) ? '' : value.toFixed(4)}</span>;
//               }
//             },
//             {
//                 title: <span style={{ color: 'black', fontWeight: 600 }}></span>,
//                 key: 'takeProfitBtn',
//                 render: (text, subOrder) => (
//                     <Button
//                       size="small"
//                       style={{ marginRight: 8, background: '#1f6d34ff', color: 'white' }}
//                       onClick={async () => {
//                         let currentPrice = '';
//                         try {
//                           const res = await fetch(`https://api.bybit.com/v5/market/tickers?category=linear&symbol=${subOrder.token}`);
//                           const data = await res.json();
//                           currentPrice = data?.result?.list?.[0]?.lastPrice || '';
//                         } catch {}
//                         setTpPopup({ visible: true, order: subOrder, currentPrice });
//                       }}
//                     >Take Profit</Button>
                  
//                 )
//               },
//             {
//               title: <span style={{ color: 'black', fontWeight: 600 }}></span>,
//               key: 'stopLoss',
//               render: (text, orderRecord) => (
//                 <Button
//                   size="small"
//                   style={ { marginLeft: 8, background: '#f21313ff', color: 'white' }}
//                   onClick={() => setSlPopup({ visible: true, order: orderRecord, currentPrice: orderRecord.markPrice || orderRecord.entryPrice })}
//                 >Stop Loss</Button>
//               )
//             },
//             { title: <span style={{ color: 'black', fontWeight: 600 }}>Size</span>, dataIndex: 'count', key: 'count', render: size => <span style={{ fontWeight: 600, color: 'black', fontSize: 15 }}>{size}</span> },
//             { 
//               title: <span style={{ color: 'black', fontWeight: 600 }}>Sold Quantity</span>, 
//               key: 'soldquantity', 
//               render: (text, mainOrder) => {
//                 const soldQty = parseFloat(mainOrder.soldQuantity) || 0;
//                 return <span style={{ color: 'black', fontWeight: 500 }}>{soldQty > 0 ? soldQty : ''}</span>;
//               }
//             },
//             {
//               title: <span style={{ color: 'black', fontWeight: 600 }}></span>,
//               key: 'closeBtn',
//               render: (text, orderRecord) => {
//                 // Calculate remaining quantity (size - sold)
//                 const size = parseFloat(orderRecord.filledQuantity) || parseFloat(orderRecord.count) || 0;
//                 const soldQty = orderRecord.soldQuantity !== undefined && orderRecord.soldQuantity !== null && orderRecord.soldQuantity !== '' ? parseFloat(orderRecord.soldQuantity) : 0;
//                 const remainingQty = size - soldQty;

//                 if (remainingQty <= 0) return null;

//                 return (
//                   <Button
//                     size="small"
//                     style={{ background: 'red', color: 'white', fontWeight: 600, border: 0 }}
//                     onClick={() => {
//                       setCloseModal({ 
//                         visible: true, 
//                         position: {
//                           ...orderRecord,
//                           size: remainingQty.toString(),
//                           symbol: orderRecord.token,
//                           markPrice: orderRecord.markPrice || record.markPrice,
//                           linkedOrderId: orderRecord._id
//                         }
//                       });
//                       setCloseQty('');
//                     }}
//                   >
//                     Close
//                   </Button>
//                 );
//               }
//             },
//           ]}
//           style={{ margin: '12px 0', background: '#fafafa', borderRadius: 6 }}
//         />
//       )}
//       {/* {subOrders.length > 0 && (
//         <div style={{ marginTop: 12 }}>
//         </div>
//       )} */}
//     </>
//   );
// },
//             rowExpandable: (record) => true,
//           }}
//         />
//       </div>
//       {/* StopLossPopup logic */}
//       <TakeProfitPopup
//         visible={tpPopup.visible}
//         order={tpPopup.order}
//         currentPrice={tpPopup.currentPrice}
//         onClose={() => setTpPopup({ visible: false, order: null, currentPrice: '' })}
//         fetchOrders={fetchOrders}
//       />
//       <StopLossPopup
//         visible={slPopup.visible}
//         order={slPopup.order}
//         currentPrice={slPopup.currentPrice}
//         onClose={() => setSlPopup({ visible: false, order: null, currentPrice: '' })}
//         fetchOrders={fetchOrders}
//       />
//       <Modal
//         open={closeModal.visible}
//         title={
//           <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' ,width:'530px'}}>
//             <span>Close Position: {closeModal.position?.symbol || ''}</span>
//             {closeModal.position && (
//               <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight:100 }}>
//                 <span style={{ fontWeight: 600, fontSize: 14, color: '#222' }}>Available Quantity:</span>
//                 <span style={{ fontWeight: 600, fontSize: 15, color: '#222', background: '#f5f5f5', borderRadius: 4, padding: '2px 8px' }}>{closeModal.position.size}</span>
//                 <Button
//                   type="text"
//                   size="small"
//                   style={{ padding: 0, marginLeft: 2 }}
//                   onClick={() => {
//                     navigator.clipboard.writeText(closeModal.position.size);
//                     toast.info('Copied!');
//                   }}
//                   icon={<svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M10 1.5A1.5 1.5 0 0 1 11.5 3v1H13a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-1.5H4A1.5 1.5 0 0 1 2.5 9V3A1.5 1.5 0 0 1 4 1.5h6zm1.5 3V3A.5.5 0 0 0 11 2.5H4A.5.5 0 0 0 3.5 3v6A.5.5 0 0 0 4 9.5h.5V6a2 2 0 0 1 2-2h5zM6 5a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H6z"/></svg>}
//                 />
//               </span>
//             )}
//           </div>
//         }
//         onCancel={() => setCloseModal({ visible: false, position: null })}
//         footer={null}
//         centered
//       >
//         {/* Percentage Buttons Row */}
//         {closeModal.position && (
//           <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
//             {[25, 40, 50, 75, 100].map(percent => (
//               <Button
//                 key={percent}
//                 style={{
//                   background: 'white',
//                   color: 'black',
//                   border: '1px solid #d1d5db',
//                   fontWeight: 600,
//                   minWidth: 48,
//                   borderRadius: 6
//                 }}
//                 onClick={() => {
//                   const size = parseFloat(closeModal.position.size) || 0;
//                   if (size > 0) {
//                     let raw = (size * percent) / 100;
//                     let intPart = Math.floor(raw);
//                     let decPart = raw - intPart;
//                     let rounded = decPart >= 0.5 ? Math.ceil(raw) : Math.floor(raw);
//                     setCloseQty(rounded.toString());
//                   }
//                 }}
//               >
//                 {percent}%
//               </Button>
//             ))}
//           </div>
//         )}
//         <div style={{ marginBottom: 12 }}>
//           <span style={{ fontWeight: 500 }}>Enter Quantity to Close:</span>
//           <Input
//             type="number"
//             min={0.0001}
//             step={0.0001}
//             value={closeQty}
//             onChange={e => setCloseQty(e.target.value)}
//             style={{ marginTop: 8, width: '100%' }}
//             placeholder="Quantity"
//             autoFocus
//           />
//         </div>
//         <Button
//           type="primary"
//           style={{ background: 'black', color: 'white', fontWeight: 600, width: '100%' }}
//           loading={closeLoading}
//           onClick={async () => {
//             if (!closeQty || isNaN(closeQty) || Number(closeQty) <= 0) {
//               toast.error('Please enter a valid quantity');
//               return;
//             }
//             setCloseLoading(true);
//             const pos = closeModal.position;
//             console.log(pos);
//             const closeSide = pos.side === 'Buy' ? 'Sell' : 'Buy';
//             try {
//               const res = await axios.post(`${BASE_URL}/api/orders/place`, {
//                 token: pos.symbol,
//                 side: closeSide,
//                 orderType: 'Market',
//                 count: closeQty,
//                 positionIdx: pos.positionIdx,
//                 reduceOnly: true,
//                 orderNature: 'closed order',
//                 entryPrice: pos.markPrice,
//                 Algorithm: 'Manual',
//                 LinkedOrderId: pos.linkedOrderId || null
//               });
//               if (res.data && res.data.bybitRes && res.data.bybitRes.retMsg === 'OK') {
//                 toast.success('Order placed successfully');
//                 setCloseModal({ visible: false, position: null });
//                 setCloseQty('');
//               } else {
//                 toast.error(res.data?.bybitRes?.retMsg || 'Order not placed');
//               }
//             } catch (err) {
//               toast.error(err?.response?.data?.error || 'Order not placed');
//             } finally {
//               setCloseLoading(false);
//             }
//           }}
//         >
//           Submit Order
//         </Button>
//       </Modal>
//       <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
//     </div>
//   );
// };

// export default CurrentPositions;  